import React, { useState, useEffect } from 'react';
import { ArrowLeft, CheckCircle2, Clock, Users, Award, Calendar, BookOpen, Shield, Globe, Briefcase, Star } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import axios from 'axios';
import { trackPageView, trackFormSubmission, trackConversion, storeUtmParams, getStoredUtmParams } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnrollmentPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    course: 'Certified Specialist in Digital Growth & Performance Marketing (CSDGPM)',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [captchaValue, setCaptchaValue] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState({ num1: 0, num2: 0, answer: 0 });

  // Generate simple math CAPTCHA
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptchaQuestion({ num1, num2, answer: num1 + num2 });
    setCaptchaValue('');
  };

  useEffect(() => {
    trackPageView('/enrollment', 'Enrollment - AI School for All');
    storeUtmParams();
    generateCaptcha();
    
    // Pre-select course based on UTM campaign
    const campaign = searchParams.get('utm_campaign');
    if (campaign === 'special_offer') {
      setFormData(prev => ({
        ...prev,
        course: 'Certified Specialist in Digital Growth & Performance Marketing (CSDGPM)'
      }));
    }
  }, [searchParams]);

  const courses = [
    'Certified Specialist in Digital Growth & Performance Marketing (CSDGPM)',
    'Certified Professional in Applied Machine Learning (CPAML)',
    'Certified Specialist in Machine Learning & Automation (CSMLA)',
    'Professional Certificate in Business & Data Analytics (PCBDA)',
    'Advanced Diploma in Applied Data Science & Predictive Modeling (ADADSPM)',
    'Certified Cloud Computing Specialist (CCCS)',
    'Professional Certificate in Digital Marketing Strategies (PCDMS)',
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCourseChange = (value) => {
    setFormData(prev => ({ ...prev, course: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate CAPTCHA
    if (parseInt(captchaValue) !== captchaQuestion.answer) {
      toast.error('Please solve the CAPTCHA correctly');
      generateCaptcha();
      return;
    }

    if (!formData.name || !formData.email || !formData.phone || !formData.city || !formData.course) {
      toast.error('Please fill in all required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const utmParams = getStoredUtmParams();
      const submissionData = {
        ...formData,
        ...utmParams
      };

      const response = await axios.post(`${API}/course-leads`, submissionData);
      
      if (response.status === 200 || response.status === 201) {
        trackFormSubmission('enrollment_form', formData.course);
        trackConversion('lead_generated', formData.course);
        // Redirect to thank you page for conversion tracking
        navigate('/thank-you');
      }
    } catch (error) {
      console.error('Enrollment error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Course details based on selection
  const getCourseDetails = () => {
    if (formData.course.includes('CSDGPM')) {
      return {
        title: 'Certified Specialist in Digital Growth & Performance Marketing (CSDGPM)',
        badge: 'Exemplar Global Certified',
        duration: '6 Months',
        hours: '150 Live Hours',
        batchStart: 'New Batches Every Month',
        enrollmentDeadline: 'New Batches Every Month',
        price: '$299',
        originalPrice: '$599',
        discount: '50% OFF till 31st Dec',
        features: [
          'Exemplar Global Certification',
          'Google Ads Certification Prep',
          '100% Placement Assistance',
          'Live Expert Sessions',
          'Real Client Projects',
          'Lifetime Community Access'
        ]
      };
    }
    // Default for other courses
    return {
      title: formData.course,
      badge: 'Professional Certificate',
      duration: '3-6 Months',
      hours: '100+ Hours',
      batchStart: 'New Batches Every Month',
      enrollmentDeadline: 'New Batches Every Month',
      price: 'Contact Us',
      originalPrice: '',
      discount: 'Early Bird Offer',
      features: [
        'Industry Expert Training',
        'Hands-on Projects',
        'Placement Assistance',
        'Course Recordings',
        'Community Access',
        'Certificate on Completion'
      ]
    };
  };

  const courseDetails = getCourseDetails();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* CHRISTMAS HERO BANNER - Block Your Seat @ $20 */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a472a 0%, #0d2818 50%, #0a1f14 100%)' }}>
        {/* Snowflakes Animation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute text-white opacity-60 animate-fall"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-20px`,
                fontSize: `${Math.random() * 20 + 10}px`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${Math.random() * 5 + 5}s`
              }}
            >
              ❄
            </div>
          ))}
        </div>

        {/* Christmas Tree Silhouette - Left */}
        <div className="absolute left-0 bottom-0 opacity-20 pointer-events-none">
          <svg width="200" height="300" viewBox="0 0 200 300" fill="none">
            <path d="M100 0 L150 80 L130 80 L170 150 L140 150 L190 250 L10 250 L60 150 L30 150 L70 80 L50 80 Z" fill="#0f3d1f"/>
            <rect x="85" y="250" width="30" height="50" fill="#4a2c2a"/>
          </svg>
        </div>

        {/* Christmas Tree Silhouette - Right */}
        <div className="absolute right-0 bottom-0 opacity-20 pointer-events-none">
          <svg width="250" height="350" viewBox="0 0 200 300" fill="none">
            <path d="M100 0 L150 80 L130 80 L170 150 L140 150 L190 250 L10 250 L60 150 L30 150 L70 80 L50 80 Z" fill="#0f3d1f"/>
            <rect x="85" y="250" width="30" height="50" fill="#4a2c2a"/>
          </svg>
        </div>

        {/* Glowing Ornaments */}
        <div className="absolute top-20 left-20 w-4 h-4 rounded-full bg-red-500 shadow-lg shadow-red-500/50 animate-pulse"></div>
        <div className="absolute top-40 right-32 w-3 h-3 rounded-full bg-yellow-400 shadow-lg shadow-yellow-400/50 animate-pulse"></div>
        <div className="absolute top-60 left-40 w-3 h-3 rounded-full bg-green-400 shadow-lg shadow-green-400/50 animate-pulse"></div>
        <div className="absolute bottom-40 right-20 w-4 h-4 rounded-full bg-red-500 shadow-lg shadow-red-500/50 animate-pulse"></div>
        <div className="absolute bottom-60 left-32 w-3 h-3 rounded-full bg-yellow-400 shadow-lg shadow-yellow-400/50 animate-pulse"></div>

        {/* Navigation */}
        <div className="container mx-auto max-w-7xl px-6 py-4 relative z-10">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </button>
            <h1 className="text-xl font-bold text-white">🎄 AI School for All</h1>
            <div className="w-24"></div>
          </div>
        </div>

        {/* Hero Content - Block Your Seat @ $20 */}
        <div className="container mx-auto max-w-7xl px-6 py-12 pb-20 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Christmas Badge */}
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full mb-6" style={{ background: 'linear-gradient(90deg, #c41e3a, #8b0000)', boxShadow: '0 0 30px rgba(196, 30, 58, 0.5)' }}>
              <span className="text-2xl">🎄</span>
              <span className="text-white text-lg font-bold">CHRISTMAS SPECIAL OFFER</span>
              <span className="text-2xl">🎁</span>
            </div>
            
            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4 leading-tight">
              <span className="text-white drop-shadow-lg" style={{ textShadow: '0 0 20px rgba(255,255,255,0.3)' }}>Block Your Seat Today @</span>
              <span className="block mt-4 text-6xl md:text-7xl lg:text-8xl" style={{ 
                background: 'linear-gradient(90deg, #ffd700, #ffec8b, #ffd700)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 20px rgba(255, 215, 0, 0.5))'
              }}>
                JUST $20
              </span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-2xl md:text-3xl mb-6">
              <span className="text-white">And get </span>
              <span className="font-black px-3 py-1 rounded" style={{ 
                background: 'linear-gradient(90deg, #c41e3a, #ff0000)',
                color: 'white',
                boxShadow: '0 0 20px rgba(255, 0, 0, 0.4)'
              }}>50% OFF</span>
              <span className="text-white"> on the complete course!</span>
            </p>
            
            {/* Offer Info Box */}
            <div className="inline-block px-8 py-4 rounded-2xl mb-8" style={{ 
              background: 'rgba(255, 255, 255, 0.1)', 
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 215, 0, 0.3)'
            }}>
              <p className="text-white text-lg font-semibold">
                ⏰ Offer valid till <span className="text-yellow-300 font-black text-xl">New Batches Every Month</span> • 
                Batch starts <span className="text-green-300 font-black text-xl">New Batches Every Month</span>
              </p>
            </div>

            {/* Trust Icons */}
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-white font-medium">Secure Payment</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Users className="w-5 h-5 text-yellow-400" />
                <span className="text-white font-medium">30,000+ Alumni</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Award className="w-5 h-5 text-red-400" />
                <span className="text-white font-medium">Exemplar Global</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Briefcase className="w-5 h-5 text-green-400" />
                <span className="text-white font-medium">100% Placement</span>
              </div>
            </div>
          </div>
        </div>

        {/* CSS for snowfall animation */}
        <style>{`
          @keyframes fall {
            0% { transform: translateY(-20px) rotate(0deg); opacity: 0; }
            10% { opacity: 0.6; }
            90% { opacity: 0.6; }
            100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
          }
          .animate-fall {
            animation: fall linear infinite;
          }
        `}</style>
      </div>

      {/* MAIN CONTENT - Form Left, Course Details Right */}
      <div className="container mx-auto max-w-7xl px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-10">
          
          {/* LEFT - Enrollment Form */}
          <div>
            <Card className="shadow-xl border-0">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-xl">
                <CardTitle className="text-2xl font-bold text-center">Enroll Now</CardTitle>
                <p className="text-center text-blue-100 text-sm">Fill the form and our team will contact you within 24 hours</p>
              </CardHeader>
              <CardContent className="p-8">
                {/* Thank You Message */}
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Thank You! 🎉</h3>
                    <p className="text-gray-600 mb-4">Your enrollment request has been submitted successfully.</p>
                    <p className="text-gray-600 mb-6">Our team will contact you within 24 hours.</p>
                    <p className="text-sm text-gray-400">Redirecting back in a few seconds...</p>
                  </div>
                ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-gray-700 font-medium">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-medium">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-gray-700 font-medium">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter your phone number"
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="city" className="text-gray-700 font-medium">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Enter your city"
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="course" className="text-gray-700 font-medium">Select Course *</Label>
                    <Select value={formData.course} onValueChange={handleCourseChange}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Choose a course" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map((course) => (
                          <SelectItem key={course} value={course}>
                            {course}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* CAPTCHA */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="w-5 h-5 text-green-600" />
                      <Label className="text-gray-700 font-medium">Security Check (CAPTCHA) *</Label>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="bg-white px-4 py-2 rounded-lg border border-gray-300 font-mono text-lg font-bold text-gray-800">
                        {captchaQuestion.num1} + {captchaQuestion.num2} = ?
                      </div>
                      <Input
                        value={captchaValue}
                        onChange={(e) => setCaptchaValue(e.target.value)}
                        placeholder="Answer"
                        className="w-24"
                        required
                      />
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={generateCaptcha}
                      >
                        Refresh
                      </Button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full py-6 text-lg font-bold bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Submitting...
                      </>
                    ) : (
                      '🎯 Submit Enrollment Request'
                    )}
                  </Button>

                  <p className="text-center text-xs text-gray-500">
                    🔒 Your information is secure and will never be shared
                  </p>
                </form>
                )}
              </CardContent>
            </Card>
          </div>

          {/* RIGHT - Course Details */}
          <div className="space-y-6">
            {/* Course Card */}
            <Card className="shadow-xl border-0 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-xs font-bold">{courseDetails.badge}</span>
                  {courseDetails.discount && (
                    <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">{courseDetails.discount}</span>
                  )}
                </div>
                <h2 className="text-xl font-bold mb-2">{courseDetails.title}</h2>
              </div>
              
              <CardContent className="p-6">
                {/* Key Info */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Clock className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-xs text-gray-500">Duration</p>
                      <p className="font-bold text-gray-900">{courseDetails.duration}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-xs text-gray-500">Training</p>
                      <p className="font-bold text-gray-900">{courseDetails.hours}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-xs text-gray-500">Batch Starts</p>
                      <p className="font-bold text-gray-900">{courseDetails.batchStart}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <Star className="w-5 h-5 text-yellow-600" />
                    <div>
                      <p className="text-xs text-gray-500">Enroll Before</p>
                      <p className="font-bold text-gray-900">{courseDetails.enrollmentDeadline}</p>
                    </div>
                  </div>
                </div>

                {/* Price */}
                {courseDetails.originalPrice && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 mb-6 border border-green-200">
                    <p className="text-sm text-gray-600 mb-1">Course Fee</p>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-400 line-through text-lg">{courseDetails.originalPrice}</span>
                      <span className="text-3xl font-black text-green-600">{courseDetails.price}</span>
                    </div>
                    <p className="text-xs text-red-600 font-semibold mt-1">⚠️ Price increases after New Batches Every Month!</p>
                  </div>
                )}

                {/* Features */}
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3">What&apos;s Included:</p>
                  <div className="space-y-2">
                    {courseDetails.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trust Indicators */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <p className="text-sm font-semibold text-gray-700 mb-4 text-center">Trusted by Industry Leaders</p>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-blue-600">30K+</p>
                  <p className="text-xs text-gray-500">Students</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">95%</p>
                  <p className="text-xs text-gray-500">Placement</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">500+</p>
                  <p className="text-xs text-gray-500">Partners</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">4.9★</p>
                  <p className="text-xs text-gray-500">Rating</p>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
              <p className="text-sm font-semibold text-gray-700 mb-3">Need Help? Contact Us</p>
              <p className="text-sm text-gray-600">📧 Email: shrey@workshoplibrary.com</p>
              <p className="text-sm text-gray-600 mt-1">Our team responds within 24 hours</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-6 px-6 bg-gray-900 text-white text-center">
        <p className="text-sm">© 2026 AI School for All. All rights reserved.</p>
        <p className="text-xs text-gray-400 mt-2">Powered by Workshop Library</p>
      </footer>
    </div>
  );
};

export default EnrollmentPage;
